import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Package,
  Plus,
  Search,
  Boxes,
  AlertTriangle,
  ArrowRight,
  ShieldCheck,
  Image as ImageIcon,
} from "lucide-react";
import Swal from "sweetalert2";
import api from "../Api/Api";

const Products = () => {
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const role = localStorage.getItem("role");

  useEffect(() => {
    const fetchProducts = async () => {
      if (role !== "admin") {
        setLoading(false);

        await Swal.fire({
          icon: "error",
          title: "Unauthorized",
          text: "You are not authorized to view this page.",
          confirmButtonColor: "#2563EB",
        });

        navigate("/");

        return;
      }

      try {
        const response = await api.get("/get_products/");

        setProducts(
          Array.isArray(response.data?.products)
            ? response.data.products
            : []
        );
      } catch (error) {
        console.error("Error fetching products:", error);

        Swal.fire({
          icon: "error",
          title: "Unable to Load Products",
          text:
            error.response?.data?.message ||
            error.response?.data?.detail ||
            "Failed to load products. Please try again.",
          confirmButtonColor: "#2563EB",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [role, navigate]);

  const handleAddProduct = () => {
    navigate("/add-product");
  };

  const handleAddStock = (product) => {
    navigate("/add-stock", {
      state: {
        product,
      },
    });
  };

  const formatPrice = (price) => {
    return Number(price || 0).toLocaleString("en-KE");
  };

  const filteredProducts = useMemo(() => {
    const search = searchTerm.trim().toLowerCase();

    if (!search) return products;

    return products.filter((product) => {
      const name = product.name?.toLowerCase() || "";
      const category = product.category?.toLowerCase() || "";

      return (
        name.includes(search) ||
        category.includes(search)
      );
    });
  }, [products, searchTerm]);

  const totalStock = useMemo(() => {
    return products.reduce(
      (total, product) =>
        total + Number(product.stock || 0),
      0
    );
  }, [products]);

  const lowStockCount = useMemo(() => {
    return products.filter(
      (product) => Number(product.stock || 0) <= 5
    ).length;
  }, [products]);

  const getStockBadge = (stock) => {
    const quantity = Number(stock || 0);

    if (quantity === 0) {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-red-50 text-red-600 text-xs font-semibold">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500" />
          Out of stock
        </span>
      );
    }

    if (quantity <= 5) {
      return (
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
          Low stock
        </span>
      );
    }

    return (
      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
        In stock
      </span>
    );
  };

  if (role !== "admin" && !loading) {
    return null;
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      {/* Header */}
      <section className="relative bg-slate-950 text-white pt-20 pb-28 px-6 overflow-hidden">
        <div className="absolute top-0 left-1/4 w-[450px] h-[450px] bg-blue-600/20 rounded-full blur-[140px]" />

        <div className="absolute -bottom-40 right-0 w-[450px] h-[450px] bg-cyan-400/10 rounded-full blur-[130px]" />

        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)
            `,
            backgroundSize: "50px 50px",
          }}
        />

        <div className="relative z-10 max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <div className="max-w-3xl">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-blue-400/20 bg-blue-500/10 text-blue-300 text-sm font-semibold mb-6">
                <ShieldCheck size={15} />
                Admin Inventory
              </div>

              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
                Product Management
              </h1>

              <p className="text-slate-300 text-lg leading-relaxed">
                Manage products, monitor inventory levels, and keep your
                Nexindi Shop stock up to date.
              </p>
            </div>

            <button
              onClick={handleAddProduct}
              className="group inline-flex items-center justify-center gap-3 bg-blue-600 text-white px-6 py-3.5 rounded-xl font-semibold hover:bg-blue-500 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-blue-950/30 transition-all duration-300"
            >
              <Plus size={18} />
              Add Product

              <ArrowRight
                size={16}
                className="transition-transform group-hover:translate-x-1"
              />
            </button>
          </div>
        </div>
      </section>

      {/* Main */}
      <main className="relative -mt-14 pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          {/* Stats */}
          <div className="grid md:grid-cols-3 gap-5 mb-8">
            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xl shadow-slate-900/5">
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-5">
                <Package size={21} />
              </div>

              <p className="text-sm text-slate-500 mb-2">
                Total Products
              </p>

              <p className="text-3xl font-bold">
                {products.length}
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xl shadow-slate-900/5">
              <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-5">
                <Boxes size={21} />
              </div>

              <p className="text-sm text-slate-500 mb-2">
                Total Units in Stock
              </p>

              <p className="text-3xl font-bold">
                {totalStock.toLocaleString()}
              </p>
            </div>

            <div className="bg-white border border-slate-200 rounded-3xl p-6 shadow-xl shadow-slate-900/5">
              <div className="w-11 h-11 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center mb-5">
                <AlertTriangle size={21} />
              </div>

              <p className="text-sm text-slate-500 mb-2">
                Low Stock Products
              </p>

              <p className="text-3xl font-bold">
                {lowStockCount}
              </p>
            </div>
          </div>

          {/* Products Card */}
          <section className="bg-white border border-slate-200 rounded-3xl shadow-xl shadow-slate-900/5 overflow-hidden">
            {/* Toolbar */}
            <div className="p-6 md:p-7 border-b border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-5">
              <div>
                <h2 className="text-xl font-bold text-slate-900">
                  Inventory
                </h2>

                <p className="text-sm text-slate-500 mt-1">
                  View and manage all products available in the shop.
                </p>
              </div>

              <div className="relative w-full md:max-w-sm">
                <Search
                  size={18}
                  className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
                />

                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) =>
                    setSearchTerm(e.target.value)
                  }
                  placeholder="Search products..."
                  className="w-full pl-11 pr-4 py-3 rounded-xl border border-slate-200 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                />
              </div>
            </div>

            {/* Loading */}
            {loading && (
              <div className="p-7 space-y-4">
                {[1, 2, 3, 4, 5].map((item) => (
                  <div
                    key={item}
                    className="h-20 rounded-2xl bg-slate-100 animate-pulse"
                  />
                ))}
              </div>
            )}

            {/* Empty */}
            {!loading && filteredProducts.length === 0 && (
              <div className="py-20 px-6 text-center">
                <div className="w-16 h-16 mx-auto rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center mb-6">
                  <Package size={28} />
                </div>

                <h3 className="text-2xl font-bold mb-3">
                  {searchTerm
                    ? "No matching products"
                    : "No products yet"}
                </h3>

                <p className="text-slate-500 max-w-md mx-auto mb-7">
                  {searchTerm
                    ? "Try searching using a different product name or category."
                    : "Add your first product to start building your shop inventory."}
                </p>

                {!searchTerm && (
                  <button
                    onClick={handleAddProduct}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700 transition"
                  >
                    <Plus size={17} />
                    Add Product
                  </button>
                )}
              </div>
            )}

            {/* Desktop Table */}
            {!loading && filteredProducts.length > 0 && (
              <>
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 px-7 py-4">
                          Product
                        </th>

                        <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 px-5 py-4">
                          Category
                        </th>

                        <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 px-5 py-4">
                          Price
                        </th>

                        <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 px-5 py-4">
                          Stock
                        </th>

                        <th className="text-left text-xs font-semibold uppercase tracking-wider text-slate-500 px-5 py-4">
                          Status
                        </th>

                        <th className="text-right text-xs font-semibold uppercase tracking-wider text-slate-500 px-7 py-4">
                          Action
                        </th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-slate-100">
                      {filteredProducts.map((product) => (
                        <tr
                          key={product.id}
                          className="hover:bg-slate-50/70 transition"
                        >
                          <td className="px-7 py-5">
                            <div className="flex items-center gap-4">
                              <div className="w-14 h-14 rounded-xl bg-slate-100 overflow-hidden flex-shrink-0 border border-slate-200">
                                {product.image ? (
                                  <img
                                    src={product.image}
                                    alt={product.name}
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center">
                                    <ImageIcon
                                      size={20}
                                      className="text-slate-300"
                                    />
                                  </div>
                                )}
                              </div>

                              <div>
                                <p className="font-semibold text-slate-900">
                                  {product.name}
                                </p>

                                {product.description && (
                                  <p className="text-xs text-slate-400 mt-1 line-clamp-1 max-w-[230px]">
                                    {product.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </td>

                          <td className="px-5 py-5 text-sm text-slate-600">
                            {product.category || "Others"}
                          </td>

                          <td className="px-5 py-5">
                            <p className="font-semibold text-slate-900">
                              KES {formatPrice(product.price)}
                            </p>
                          </td>

                          <td className="px-5 py-5">
                            <p className="font-semibold">
                              {Number(product.stock || 0).toLocaleString()}
                            </p>
                          </td>

                          <td className="px-5 py-5">
                            {getStockBadge(product.stock)}
                          </td>

                          <td className="px-7 py-5 text-right">
                            <button
                              onClick={() =>
                                handleAddStock(product)
                              }
                              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-blue-50 text-blue-600 text-sm font-semibold hover:bg-blue-600 hover:text-white transition"
                            >
                              <Plus size={15} />
                              Add Stock
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Cards */}
                <div className="md:hidden divide-y divide-slate-100">
                  {filteredProducts.map((product) => (
                    <div
                      key={product.id}
                      className="p-5"
                    >
                      <div className="flex gap-4">
                        <div className="w-20 h-20 rounded-2xl bg-slate-100 overflow-hidden flex-shrink-0 border border-slate-200">
                          {product.image ? (
                            <img
                              src={product.image}
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <ImageIcon
                                size={23}
                                className="text-slate-300"
                              />
                            </div>
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-slate-900">
                            {product.name}
                          </p>

                          <p className="text-xs text-slate-500 mt-1">
                            {product.category || "Others"}
                          </p>

                          <p className="font-bold text-blue-600 mt-2">
                            KES {formatPrice(product.price)}
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3 mt-5">
                        <div className="bg-slate-50 rounded-xl p-3">
                          <p className="text-xs text-slate-400 mb-1">
                            Stock
                          </p>

                          <p className="font-semibold">
                            {Number(
                              product.stock || 0
                            ).toLocaleString()}
                          </p>
                        </div>

                        <div className="flex items-center">
                          {getStockBadge(product.stock)}
                        </div>
                      </div>

                      <button
                        onClick={() =>
                          handleAddStock(product)
                        }
                        className="w-full mt-4 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-blue-600 text-white font-semibold text-sm hover:bg-blue-700 transition"
                      >
                        <Plus size={16} />
                        Add Stock
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </section>
        </div>
      </main>
    </div>
  );
};

export default Products;